import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { Button, IconButton, TextButton } from '@fiverr-private/button';
import { Text } from '@fiverr-private/typography';
import { FlagIcon } from '@fiverr-private/icons';
import { Tooltip } from '@fiverr-private/tooltip';
import { I18n } from '@fiverr-private/i18n-react';
import { theme } from '@fiverr-private/theme';
import { getContext } from '@fiverr-private/fiverr_context';
import { triggerSignIn } from '@fiverr-private/identification_triggers';
import { useShouldOpenUserActivationModal, CodeActivationModal } from '@fiverr-private/code_activation';
import pathfinder from '@fiverr-private/pathfinder';
import { useReportingContext } from '../../Context';
import { reportClickedOnItemReport } from '../../../utils/bianka';
import { isItemEligibleToReport, useCountOnMount, useMetricCounter } from '../../../hooks';

const { colors } = theme;

interface ReportAnIssueButtonProps {
    variant?: 'icon' | 'button' | 'text';
    shouldTriggerAuth?: boolean;
    shouldTriggerActivation?: boolean;
    entityId?: string;
}

export const ReportAnIssueButton = ({
    variant = 'icon',
    shouldTriggerAuth = false,
    shouldTriggerActivation = false,
    entityId: entityIdFromProps,
}: ReportAnIssueButtonProps) => {
    const {
        openModal,
        eligibilityList,
        entityType,
        entityId: entityIdFromContext,
        sellerId,
        buyerId,
    } = useReportingContext();
    const entityId = entityIdFromProps ?? (entityIdFromContext as string);
    const [showActivationModal, setShowActivationModal] = useState(false);
    const [userEmail, setUserEmail] = useState(null);
    const shouldOpenUserActivationModal = useShouldOpenUserActivationModal();
    const onActivationSuccess = useCallback(() => {
        window.location.reload();
    }, []);
    const { userId } = getContext();

    const isReportingDisabled =
        !userId && shouldTriggerAuth ? false : !isItemEligibleToReport(eligibilityList, entityId);

    const label = isReportingDisabled ? 'content_reporting.report_submitted' : 'content_reporting.report_an_issue_item';

    const flagColor = isReportingDisabled ? colors.grey_900 : colors.grey_1200;
    const textColor = isReportingDisabled ? 'bodySecondary' : 'bodyPrimary';
    const count = useMetricCounter();
    useCountOnMount(`viewed_report_button_${isReportingDisabled ? 'disabled' : 'enabled'}`);

    const handleButtonClick = useCallback(async () => {
        reportClickedOnItemReport({
            itemId: entityId,
            entityType,
            sellerId,
            buyerId,
        });
        count('content_reporting.report_clicked');

        if (!userId && shouldTriggerAuth) {
            await triggerSignIn({
                modalOptions: { isSticky: true, skipQuestionnaire: true },
            });
            return;
        }

        if (userId && shouldTriggerActivation) {
            const needActivation = await shouldOpenUserActivationModal();

            if (needActivation) {
                const url = pathfinder('user_name_and_email');
                const { data } = await axios.get(url);
                setUserEmail(data.email);

                setShowActivationModal(true);
                return;
            }
        }

        openModal(entityId);
    }, [
        count,
        buyerId,
        userId,
        entityId,
        entityType,
        openModal,
        sellerId,
        shouldTriggerAuth,
        shouldTriggerActivation,
        shouldOpenUserActivationModal,
    ]);

    return (
        <>
            {variant === 'icon' && (
                <Tooltip content={<I18n k={label} />}>
                    <IconButton disabled={isReportingDisabled} onClick={handleButtonClick} aria-label="Flag">
                        <FlagIcon color={flagColor} />
                    </IconButton>
                </Tooltip>
            )}
            {variant === 'button' && (
                <Button variant="ghost" disabled={isReportingDisabled} onClick={handleButtonClick}>
                    <FlagIcon color={flagColor} />
                    <Text color={textColor}>
                        <I18n k={label} />
                    </Text>
                </Button>
            )}
            {variant === 'text' && (
                <TextButton disabled={isReportingDisabled} onClick={handleButtonClick}>
                    <FlagIcon color={flagColor} />
                    <Text color={textColor}>
                        <I18n k={label} />
                    </Text>
                </TextButton>
            )}
            {showActivationModal && userEmail && (
                <CodeActivationModal
                    isOpen
                    shouldSendActivationCodeOnOpen
                    postActivationDelay={3000}
                    email={userEmail}
                    onSuccess={onActivationSuccess}
                />
            )}
        </>
    );
};
